import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ReportsService } from '../api/ReportsAPI';
import { PaymentsService } from '../api/PaymentsAPI';
import { RemittancesService } from '../api/RemittancesAPI';
import { useLightMode, useTaxMode, shownQuote, toDateStr } from '../utils';
import { Header } from '../components/Header/Header';
import { DateRangeFilter } from '../components/DateRangeFilter/DateRangeFilter';
import { AppointmentDrawer } from '../components/AppointmentDrawer/AppointmentDrawer';
import { SetPaymentMethodModal } from '../components/SetPaymentMethodModal/SetPaymentMethodModal';
import s from '../styles/staff.module.css';
import d from '../styles/dashboard.module.css';

const money = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const parseMoney = (text) => {
  const n = parseFloat(String(text ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

const fmtDate = (v) => (v
  ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
  : '—');

const fmtLongDate = (v) => (v
  ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  : '—');

// How an account is described under its name: how many cars it covers, plus
// whichever fact matters most about the state it is in.
function accountNote(a) {
  const cars = `${a.cars} car${a.cars === 1 ? '' : 's'}`;
  if (a.openRemittance) {
    return `${cars} · ${money(a.openRemittance.unallocatedCents)} remittance open`;
  }
  if (a.pastSixtyLines > 0) {
    return `${cars} · ${a.pastSixtyLines} line${a.pastSixtyLines === 1 ? '' : 's'} past 60 days`;
  }
  return `${cars} · oldest ${a.oldestDays} day${a.oldestDays === 1 ? '' : 's'}`;
}

// Record money arriving from an account. The amount is what the cheque or
// transfer was actually for, so it is entered in money terms, tax included.
function RecordRemittanceModal({ account, onClose, onSaved }) {
  const [amountText, setAmountText] = useState('$0.00');
  const [receivedAt, setReceivedAt] = useState(toDateStr(new Date()));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const amount = parseMoney(amountText);

  const save = async () => {
    if (amount <= 0 || saving) return;
    setSaving(true);
    setError('');
    try {
      const res = await RemittancesService.record({
        account: account.account, amount, receivedAt, note: note.trim() || undefined,
      });
      onSaved(res);
    } catch (err) {
      setError(err?.message || 'Could not record that remittance');
      setSaving(false);
    }
  };

  return (
    <div className={s.remitOverlay} onClick={saving ? undefined : onClose}>
      <div className={s.remitModal} onClick={(e) => e.stopPropagation()}>
        <div className={s.remitModalHead}>
          <div>
            <div className={s.ledgerTileLabel}>Record remittance</div>
            <div className={s.acctName}>{account.label || account.account}</div>
          <div className={s.ledgerTileNote}>{account.kind}</div>
          </div>
          <button className={s.remitClose} onClick={onClose} disabled={saving} aria-label="Close">×</button>
        </div>

        <label className={s.remitField}>
          <span className={s.ledgerTileLabel}>Amount received</span>
          <input
            className={s.remitInput}
            value={amountText}
            autoFocus
            onChange={(e) => setAmountText(e.target.value)}
            onBlur={() => setAmountText(money(parseMoney(amountText)))}
          />
        </label>

        <label className={s.remitField}>
          <span className={s.ledgerTileLabel}>Received on</span>
          <input type="date" className={s.remitInput} value={receivedAt}
                 max={toDateStr(new Date())}
                 onChange={(e) => setReceivedAt(e.target.value)} />
        </label>

        <label className={s.remitField}>
          <span className={s.ledgerTileLabel}>Note</span>
          <input className={s.remitInput} value={note} placeholder="Cheque number, reference…"
                 onChange={(e) => setNote(e.target.value)} />
        </label>

        {error && <div className={s.remitError}>{error}</div>}

        <div className={s.remitActions}>
          <button className={s.remitCancel} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={s.remitSave} onClick={save} disabled={saving || amount <= 0}>
            {saving ? 'Recording…' : `Record ${money(amount)}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ReceivablesPage() {
  const [lightMode, toggleLightMode] = useLightMode();
  // An invoiced line records the pre-tax quote, with its tax alongside.
  const [taxMode, setTaxMode] = useTaxMode();
  const [range, setRange] = useState({ start: '', end: '' });
  const [openAccount, setOpenAccount] = useState(null);
  const [settling, setSettling] = useState(null);
  const [recordFor, setRecordFor] = useState(null);
  const [detailAppt, setDetailAppt] = useState(null);
  const [payAppt, setPayAppt] = useState(null);
  const [payReturnTo, setPayReturnTo] = useState(null);
  // Proposed split, keyed by line: cents to put against each one. Held here
  // until "Settle account" commits it, so nothing is written half-done.
  const [draft, setDraft] = useState({});
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const onRangeChange = useCallback((r) => setRange(r), []);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error: loadError } = useQuery({
    queryKey: ['report-receivables', range.start, range.end],
    queryFn: () => ReportsService.receivables(range.start, range.end),
    enabled: !!range.start && !!range.end,
    placeholderData: (prev) => prev,
  });

  const accounts = useMemo(() => data?.accounts || [], [data]);
  const buckets = data?.buckets || [];
  const selected = accounts.find(a => a.account === openAccount) || null;
  const remittance = selected?.openRemittance || null;

  // A different account, or a fresh remittance, starts from nothing applied.
  useEffect(() => { setDraft({}); setError(''); }, [openAccount, remittance?.idRemittance]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['report-receivables', range.start, range.end] });
    // Settling clears it from the overdue bucket.
    await queryClient.invalidateQueries({ queryKey: ['attention'] });
  };

  const settle = async (idPayment) => {
    setSettling(idPayment);
    try {
      await PaymentsService.settle(idPayment, null);
      await refresh();
    } finally {
      setSettling(null);
    }
  };

  const draftTotal = Object.values(draft).reduce((n, v) => n + (v || 0), 0);
  const stillUnallocated = (remittance?.unallocatedCents || 0) - draftTotal;

  // Oldest first, which is how an account's own statement reads. Worked out on
  // the server so the order and the caps match what it will accept.
  const autoAllocate = async () => {
    if (!remittance) return;
    setError('');
    try {
      const res = await RemittancesService.proposal(remittance.idRemittance);
      const next = {};
      for (const a of res.allocations || []) next[a.idPayment] = a.amount;
      setDraft(next);
    } catch (err) {
      setError(err?.message || 'Could not work out an allocation');
    }
  };

  const commit = async () => {
    if (!remittance || draftTotal <= 0 || applying) return;
    setApplying(true);
    setError('');
    try {
      const allocations = Object.entries(draft)
        .filter(([, amount]) => amount > 0)
        .map(([idPayment, amount]) => ({ idPayment: Number(idPayment), amount }));
      await RemittancesService.allocate(remittance.idRemittance, allocations);
      setDraft({});
      await refresh();
    } catch (err) {
      setError(err?.message || 'Could not allocate that remittance');
    }
    setApplying(false);
  };

  return (
    <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
      <Header lightMode={lightMode} toggleLightMode={toggleLightMode}
        taxMode={taxMode} onTaxModeChange={setTaxMode} />

      <div className={s.body} style={{ gridTemplateColumns: '1fr' }}>
        <div>
          <div className={s.rcvHead}>
            <div>
              <div className={s.sectionLabel} style={{ marginBottom: 2 }}>
                Invoiced and hotel-billed work
              </div>
              <h1 className={s.rcvTitle}>Receivables</h1>
            </div>
            <div className={s.rcvBuckets}>
              {buckets.map(b => (
                <div key={b.id} className={s.rcvBucket}>
                  <div className={s.rcvBucketLabel}>{b.label}</div>
                  <div className={`${s.rcvBucketValue} ${
                    b.id === 'old' ? s.ledgerRed : b.id === 'mid' ? s.ledgerAmber : ''}`}>
                    {money(shownQuote(b.cents, b.taxCents, taxMode))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={s.filterRow}>
            <div className={s.ledgerIntro} style={{ marginLeft: 0 }}>
              {accounts.filter(a => a.openCents > 0).length} of {accounts.length} account{accounts.length === 1 ? '' : 's'} with money outstanding
            </div>
            <DateRangeFilter onChange={onRangeChange} />
          </div>

          {isLoading && <div className={s.ledgerIntro}>Loading…</div>}

          {/* Without this a failed request renders an empty page, which reads
              as "nothing is owed" rather than "this did not load". */}
          {isError && (
            <div className={s.remitError}>
              Couldn’t load receivables{loadError?.message ? ` — ${loadError.message}` : ''}.
            </div>
          )}
          {!isLoading && !isError && !accounts.length && (
            <div className={s.ledgerIntro}>No accounts to show for this range.</div>
          )}

          <div className={s.acctGrid}>
            {accounts.map(a => (
              <div key={a.account}
                   className={`${s.acctCard} ${openAccount === a.account ? s.acctCardOpen : ''}`}>
                <div className={s.ledgerTileLabel}>{a.kind}</div>
                <div className={s.acctName}>{a.label || a.account}</div>
                <div className={s.acctAmount}>
                  {money(shownQuote(a.openCents, a.openTaxCents, taxMode))}
                </div>
                <div className={s.ledgerTileNote}>{accountNote(a)}</div>
                <div className={s.acctBtnRow}>
                  <button className={s.acctBtn} onClick={() => setRecordFor(a)}>
                    Record remittance
                  </button>
                  <button
                    className={s.acctBtnGhost}
                    onClick={() => setOpenAccount(openAccount === a.account ? null : a.account)}
                  >
                    {openAccount === a.account ? 'Hide' : a.openRemittance ? 'Allocate' : 'Show lines'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {selected && remittance && (
            <div className={s.allocPanel}>
              <div className={s.allocHead}>
                <span className={s.allocLabel}>Allocate remittance</span>
                <span className={s.allocSub}>
                  {selected.label || selected.account} · {selected.kind.toLowerCase()} · {money(remittance.amountCents)} received {fmtLongDate(remittance.receivedAt)}
                </span>
                <span className={`${s.allocLeft} ${stillUnallocated < 0 ? s.ledgerRed : ''}`}>
                  {money(stillUnallocated)} unallocated
                </span>
              </div>

              <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Date</th><th>Customer</th><th>Package</th>
                    <th className={s.num}>Open</th>
                    <th className={s.num}>Applied</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.lines.map(l => (
                    <tr key={l.idPayment}>
                      <td>{fmtDate(l.date)}</td>
                      <td><strong>{l.customer || '—'}</strong></td>
                      <td className={s.cellWrap}>{l.services.length ? l.services.join(', ') : '—'}</td>
                      {/* Cash terms throughout this panel: a remittance is real
                          money, so it is matched against the invoice plus tax
                          however the header switch is set. */}
                      <td className={s.num}>{money(l.remainingCents)}</td>
                      <td className={s.num}>
                        <input
                          className={s.allocInput}
                          value={money(draft[l.idPayment] || 0)}
                          onChange={(e) => {
                            const v = Math.min(parseMoney(e.target.value), l.remainingCents);
                            setDraft(d => ({ ...d, [l.idPayment]: v }));
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              {error && <div className={s.remitError}>{error}</div>}

              <div className={s.allocActions}>
                <button className={s.remitSave} onClick={autoAllocate}>
                  Auto-allocate oldest first
                </button>
                <button
                  className={s.remitCancel}
                  onClick={commit}
                  disabled={applying || draftTotal <= 0 || stillUnallocated < 0}
                >
                  {applying ? 'Settling…' : 'Settle account'}
                </button>
              </div>
            </div>
          )}

          {selected && !remittance && (
            <div style={{ marginTop: 16 }}>
              <div className={s.sectionLabel}>
                {selected.label || selected.account} · {selected.kind.toLowerCase()}
                <span className={s.ledgerIntro}>
                  {money(shownQuote(selected.openCents, selected.openTaxCents, taxMode))} open across {selected.cars} car{selected.cars === 1 ? '' : 's'}
                </span>
              </div>
              <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Date</th><th>Customer</th><th>Package</th>
                    <th className={s.num}>Open</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {selected.lines.map(l => (
                    <tr
                      key={l.idPayment}
                      className={l.appt ? s.rowClickable : ''}
                      onClick={l.appt ? () => setDetailAppt(l.appt) : undefined}
                    >
                      <td className={l.ageDays > 60 ? s.ledgerRed : ''}>{fmtDate(l.date)}</td>
                      <td><strong>{l.customer || '—'}</strong></td>
                      <td className={s.cellWrap}>
                        {l.services.length ? l.services.join(', ') : '—'}
                        {l.invoiceNumber && <span className={s.subCell}>{l.invoiceNumber}</span>}
                      </td>
                      <td className={s.num}>
                        <strong>{money(shownQuote(l.openCents, l.taxCents, taxMode))}</strong>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {/* <button className={s.actionBtn} disabled={settling === l.idPayment}
                                onClick={() => settle(l.idPayment)}>
                          {settling === l.idPayment ? 'Settling…' : 'Mark paid'}
                        </button> */}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={lightMode ? d.lightRoot : undefined} style={{ display: 'contents' }}>
        {detailAppt && (
          <AppointmentDrawer
            appt={detailAppt}
            staff={[]}
            onClose={() => setDetailAppt(null)}
            onVerified={refresh}
            onTakePayment={(a) => { setDetailAppt(null); setPayReturnTo(a); setPayAppt(a); }}
            onChangeMethod={(a) => { setDetailAppt(null); setPayReturnTo(a); setPayAppt(a); }}
          />
        )}

        {payAppt && (
          <SetPaymentMethodModal
            appt={payAppt}
            onClose={() => {
              setPayAppt(null);
              if (payReturnTo) setDetailAppt(payReturnTo);
              setPayReturnTo(null);
            }}
            onSaved={refresh}
          />
        )}
      </div>

      {recordFor && (
        <RecordRemittanceModal
          account={recordFor}
          onClose={() => setRecordFor(null)}
          onSaved={async () => {
            const name = recordFor.account;
            setRecordFor(null);
            await refresh();
            // Drop straight into allocating what just arrived.
            setOpenAccount(name);
          }}
        />
      )}
    </div>
  );
}
